import { NetError } from './error'
import { getRangeValue, RequestStats } from './shared'
import { ResponseType } from './types'
import { createPublicPromise } from '../utils'

export class FetchLoader {
  _abortController = null
  _stats = null
  _timeoutTimer = null
  _reader = null
  _response = null

  load ({
    url,
    timeout, // ms
    responseType,
    onProgress,
    onTimeout,
    range,
    responseFilter,
    ...init
  }) {
    this._abortController = new AbortController()
    const stats = this._stats = new RequestStats()
    init.signal = this._abortController.signal
    let isTimeout = false
    clearTimeout(this._timeoutTimer)

    const rangeValue = getRangeValue(range)
    if (rangeValue) {
      const headers = init.headers = init.headers || new Headers()
      if (headers instanceof Headers) {
        headers.append('Range', rangeValue)
      } else {
        headers.Range = rangeValue
      }
    }

    if (timeout) {
      this._timeoutTimer = setTimeout(() => {
        isTimeout = true
        this.cancel()
        if (onTimeout) {
          const error = new NetError(url, init)
          error.isTimeout = true
          onTimeout(error)
        }
      }, timeout)
    }

    const startTime = performance.now()
    return fetch(url, init).then(async (response) => {
      clearTimeout(this._timeoutTimer)
      stats.loadTime = performance.now() - startTime
      this._response = response
      if (responseFilter) {
        response = responseFilter(url, response, stats) || response
      }

      if (!response.ok) {
        throw new NetError(url, init, response, 'bad network response')
      }

      let age = response.headers.get('age')
      age = (age != null) ? parseFloat(age) : null
      let contentLength = parseInt(response.headers.get('Content-Length') || '0', 10)
      if (isNaN(contentLength)) contentLength = 0

      let data
      let dataLength = contentLength
      if (responseType === ResponseType.TEXT) {
        data = await response.text()
      } else if (responseType === ResponseType.JSON) {
        data = await response.json()
      } else {
        if (onProgress) {
          await this._loadChunk(response, onProgress, startTime)
        } else {
          data = await response.arrayBuffer()
          data = new Uint8Array(data)
          if (!dataLength) dataLength = data.byteLength
        }
      }

      stats.totalTime = performance.now() - startTime

      if (stats.aborted) data = undefined

      return {
        data,
        response,
        stats,
        contentLength,
        age
      }
    }).catch((error) => {
      clearTimeout(this._timeoutTimer)
      if (stats.aborted && !isTimeout) return
      error = error instanceof NetError ? error : new NetError(url, init)
      error.isTimeout = isTimeout
      throw error
    })
  }

  async cancel () {
    if (this._stats) this._stats.aborted = true
    if (this._response) {
      try {
        await this._response.body.cancel()
        if (this._reader) {
          await this._reader.cancel()
        }
      } catch (error) {
        // ignore
      }
      this._response = this._reader = null
    }

    if (this._abortController) {
      try {
        this._abortController.abort()
      } catch (error) {
        // ignore
      }
      this._abortController = null
    }
  }

  _loadChunk (response, onProgress, startTime) {
    const stats = this._stats
    const reader = this._reader = response.body.getReader()
    let data

    const prom = createPublicPromise()

    let endTime
    const pump = async () => {
      try {
        data = await reader.read()
      } catch (e) {
        // request aborted
        return prom.resolve()
      }

      if (stats.aborted) {
        prom.resolve()
        onProgress(undefined, true, response, stats)
        return
      }

      endTime = performance.now()
      onProgress(data.value, data.done, response, stats, endTime - startTime)
      startTime = endTime
      if (!data.done) {
        pump()
      } else {
        prom.resolve()
      }
    }
    pump()
    return prom
  }

  static isSupported () {
    if (typeof ReadableStream !== 'undefined') {
      try {
        new ReadableStream({}) // eslint-disable-line no-new
      } catch (e) {
        return false
      }
    } else {
      return false
    }
    return !!(typeof fetch !== 'undefined' && typeof AbortController !== 'undefined')
  }
}
